import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { getProducts, getCardgames, getProductTypes, updateStoreStock } from '../../services/supabaseService';
import { useNotification } from '../../components/member/NotificationProvider';
import { isProductPreSale } from '../../types';

interface AddCatalogProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  storeId: number | string;
  existingStock: Array<{
    product_id: number;
    quantity: number;
    store_price: number | null;
    pre_sale?: boolean;
    products?: any;
  }>;
  onStockUpdated: (item: {
    product_id: number;
    quantity: number;
    store_price: number;
    pre_sale: boolean;
    product: any;
  }) => void;
}

export const AddCatalogProductModal: React.FC<AddCatalogProductModalProps> = ({
  isOpen,
  onClose,
  storeId,
  existingStock,
  onStockUpdated
}) => {
  const { showNotification } = useNotification();
  const [products, setProducts] = useState<any[]>([]);
  const [games, setGames] = useState<any[]>([]);
  const [productTypes, setProductTypes] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGameId, setSelectedGameId] = useState<number | 'all'>('all');
  const [selectedTypeId, setSelectedTypeId] = useState<number | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'unadded' | 'added'>('unadded');

  // Form states per product: { [productId]: { quantity, price, preSale, saving, saved } }
  const [itemStates, setItemStates] = useState<Record<number, {
    quantity: number;
    price: number;
    preSale: boolean;
    saving?: boolean;
    saved?: boolean;
  }>>({});

  // Fast map of existing stock for O(1) checks
  const existingStockMap = useMemo(() => {
    const map = new Map<number, { quantity: number; price: number | null; pre_sale: boolean }>();
    existingStock.forEach(item => {
      map.set(item.product_id, {
        quantity: item.quantity,
        price: item.store_price,
        pre_sale: !!item.pre_sale
      });
    });
    return map;
  }, [existingStock]);

  useEffect(() => {
    if (!isOpen) return;

    let isMounted = true;
    setLoading(true);

    const unsub = getProducts((data) => {
      if (!isMounted) return;
      // Filter out tickets/subscriptions
      const validProducts = data.filter(p => {
        const isTicket = p.product_type_id === 52 || p.product_type_id === 3 || p.product_types?.name === 'Ticket' || p.product_types?.name === 'Ingresso' || p.product_types?.name === 'Inscrição';
        return !isTicket;
      });
      setProducts(validProducts);
      setLoading(false);
    });

    const fetchMeta = async () => {
      try {
        const [gameList, typesList] = await Promise.all([
          getCardgames(),
          getProductTypes()
        ]);
        if (isMounted) {
          setGames(gameList || []);
          setProductTypes(typesList || []);
        }
      } catch (e) {
        console.error("Error fetching catalog metadata:", e);
      }
    };

    fetchMeta();

    return () => {
      isMounted = false;
      unsub();
    };
  }, [isOpen]);

  // Initialize or get form state for a product
  const getFormState = (product: any) => {
    if (itemStates[product.id]) {
      return itemStates[product.id];
    }
    const inStock = existingStockMap.get(product.id);
    const rawVal = inStock?.price !== null && inStock?.price !== undefined
      ? Number(inStock.price)
      : Number(product.msrp || product.mspr || 0.05);
    const defaultPrice = Math.max(0.05, Math.round(rawVal * 100) / 100);

    return {
      quantity: inStock?.quantity ?? 1,
      price: defaultPrice,
      preSale: inStock?.pre_sale ?? false,
      saving: false,
      saved: false
    };
  };

  const handleFieldChange = (productId: number, field: 'quantity' | 'price' | 'preSale', value: any) => {
    setItemStates(prev => {
      const current = prev[productId] || getFormState(products.find(p => p.id === productId));
      return {
        ...prev,
        [productId]: {
          ...current,
          [field]: value,
          saved: false
        }
      };
    });
  };

  const handleQuickAdd = async (product: any) => {
    if (!storeId) {
      showNotification("Loja não identificada.", "error");
      return;
    }

    const state = getFormState(product);
    const quantity = Math.max(1, Number(state.quantity) || 1);
    const price = Number(state.price);

    if (isNaN(price) || price < 0.05) {
      showNotification("O preço mínimo no estoque é de R$ 0,05 (5 centavos).", "error");
      return;
    }

    // Set saving
    setItemStates(prev => ({
      ...prev,
      [product.id]: {
        ...state,
        saving: true
      }
    }));

    try {
      const finalPrice = Math.max(0.05, price);
      const res = await updateStoreStock(storeId, product.id, quantity, finalPrice, state.preSale);

      if (!res || (res as any).error) {
        throw new Error((res as any)?.error || "Erro ao salvar no estoque.");
      }

      // Success feedback
      setItemStates(prev => ({
        ...prev,
        [product.id]: {
          ...state,
          quantity,
          price: finalPrice,
          saving: false,
          saved: true
        }
      }));

      // Notify parent to immediately reflect in ManageStock table
      onStockUpdated({
        product_id: product.id,
        quantity,
        store_price: finalPrice,
        pre_sale: state.preSale,
        product: {
          id: product.id,
          name: product.beauty_name || product.name,
          slug: product.slug,
          image_url: product.image_url,
          game_id: product.game_id,
          games: product.games
        }
      });

      showNotification(`${product.beauty_name || product.name} adicionado ao estoque!`, "success");

      // Clear saved tick after 3 seconds
      setTimeout(() => {
        setItemStates(prev => {
          if (!prev[product.id]) return prev;
          return {
            ...prev,
            [product.id]: {
              ...prev[product.id],
              saved: false
            }
          };
        });
      }, 3000);
    } catch (err: any) {
      setItemStates(prev => ({
        ...prev,
        [product.id]: {
          ...state,
          saving: false
        }
      }));
      showNotification(err.message || "Falha ao adicionar produto ao estoque.", "error");
    }
  };

  // Filtered product types dynamically based on selected game
  const availableProductTypes = useMemo(() => {
    const nonTicket = productTypes.filter((t: any) => {
      const isTicket = 
        Number(t.category_id) === 3 || 
        Number(t.id) === 52 || 
        Number(t.id) === 3 ||
        t.name?.toLowerCase() === 'ticket' || 
        t.name?.toLowerCase() === 'ingresso' || 
        t.name?.toLowerCase() === 'inscrição' ||
        t.name?.toLowerCase() === 'inscricao';
      return !isTicket;
    });

    if (selectedGameId === 'all') return nonTicket;
    const targetGameId = Number(selectedGameId);
    return nonTicket.filter((t: any) => t.game_id === null || Number(t.game_id) === targetGameId);
  }, [productTypes, selectedGameId]);

  // Reset selectedTypeId if no longer available in selected game
  useEffect(() => {
    if (selectedTypeId !== 'all') {
      const isStillAvailable = availableProductTypes.some((t: any) => Number(t.id) === Number(selectedTypeId));
      if (!isStillAvailable) {
        setSelectedTypeId('all');
      }
    }
  }, [selectedGameId, availableProductTypes, selectedTypeId]);

  // Filtered products
  const filteredProducts = useMemo(() => {
    return products.filter(product => {
      // Search
      const search = searchTerm.trim().toLowerCase();
      if (search) {
        const name = (product.name || '').toLowerCase();
        const beautyName = (product.beauty_name || '').toLowerCase();
        const typeName = (product.product_type_name || product.product_types?.name || '').toLowerCase();
        const gameName = (product.game_name || product.games?.name || '').toLowerCase();
        if (!name.includes(search) && !beautyName.includes(search) && !typeName.includes(search) && !gameName.includes(search)) {
          return false;
        }
      }

      // Game
      if (selectedGameId !== 'all') {
        const gameId = product.game_id || product.games?.id;
        if (Number(gameId) !== Number(selectedGameId)) return false;
      }

      // Type
      if (selectedTypeId !== 'all') {
        const typeId = product.product_type_id || product.product_types?.id;
        if (Number(typeId) !== Number(selectedTypeId)) return false;
      }

      // Status
      const isInStock = existingStockMap.has(product.id);
      if (statusFilter === 'unadded' && isInStock) return false;
      if (statusFilter === 'added' && !isInStock) return false;

      return true;
    });
  }, [products, searchTerm, selectedGameId, selectedTypeId, statusFilter, existingStockMap]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6 bg-slate-950/90 overflow-y-auto">
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.98 }}
          transition={{ duration: 0.15 }}
          className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-5xl max-h-[92vh] flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div className="p-5 sm:p-6 border-b border-slate-800 bg-slate-900/90 flex items-center justify-between shrink-0">
            <div>
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center text-purple-400">
                  <i className="fas fa-bag-shopping text-sm"></i>
                </div>
                <h2 className="text-xl font-black text-white italic tracking-tight uppercase">
                  Adicionar do Catálogo ao Estoque
                </h2>
              </div>
              <p className="text-xs text-slate-400 mt-1">
                Selecione produtos oficiais do catálogo Cardumy e defina o preço e quantidade para adicioná-los instantaneamente ao estoque da sua loja.
              </p>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 rounded-2xl bg-slate-800/80 hover:bg-slate-800 border border-slate-700/50 flex items-center justify-center text-slate-400 hover:text-white transition-colors"
            >
              <i className="fas fa-times"></i>
            </button>
          </div>

          {/* Search and Filters Bar */}
          <div className="p-4 sm:p-5 border-b border-slate-800/80 bg-slate-950/40 space-y-3 shrink-0">
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              {/* Search */}
              <div className="flex-1 relative">
                <i className="fas fa-search absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 text-xs"></i>
                <input
                  type="text"
                  placeholder="Buscar pelo nome do produto, booster box, deck, etc..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  autoFocus
                  className="w-full bg-slate-900 border border-slate-800 rounded-xl pl-9 pr-9 py-2.5 text-xs text-white placeholder:text-slate-500 font-medium focus:outline-none focus:border-purple-500 transition-colors"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white text-xs"
                  >
                    <i className="fas fa-times-circle"></i>
                  </button>
                )}
              </div>

              {/* Game Select */}
              <div className="sm:w-44 shrink-0">
                <select
                  value={selectedGameId}
                  onChange={(e) => setSelectedGameId(e.target.value === 'all' ? 'all' : Number(e.target.value))}
                  className="w-full bg-slate-900 border border-slate-800 rounded-xl px-3 py-2.5 text-xs font-bold text-slate-300 focus:outline-none focus:border-purple-500 uppercase tracking-wide cursor-pointer"
                >
                  <option value="all">Todos os Jogos</option>
                  {games.map(g => (
                    <option key={g.id} value={g.id}>{g.name}</option>
                  ))}
                </select>
              </div>

              {/* Type Select */}
              <div className="sm:w-44 shrink-0">
                <select
                  value={selectedTypeId}
                  onChange={(e) => setSelectedTypeId(e.target.value === 'all' ? 'all' : Number(e.target.value))}
                  className="w-full bg-slate-900 border border-slate-800 rounded-xl px-3 py-2.5 text-xs font-bold text-slate-300 focus:outline-none focus:border-purple-500 uppercase tracking-wide cursor-pointer"
                >
                  <option value="all">Todas as Subcategorias</option>
                  {availableProductTypes.map((t: any) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Quick Status Filter Tabs */}
            <div className="flex items-center justify-between gap-2 pt-1 flex-wrap">
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => setStatusFilter('unadded')}
                  className={`px-3 py-1.5 rounded-lg text-[11px] font-black uppercase tracking-wider transition-all ${
                    statusFilter === 'unadded'
                      ? 'bg-purple-600 text-white shadow-md shadow-purple-600/20'
                      : 'bg-slate-800/60 text-slate-400 hover:text-slate-200'
                  }`}
                >
                  Fora do Estoque ({products.filter(p => !existingStockMap.has(p.id)).length})
                </button>
                <button
                  onClick={() => setStatusFilter('all')}
                  className={`px-3 py-1.5 rounded-lg text-[11px] font-black uppercase tracking-wider transition-all ${
                    statusFilter === 'all'
                      ? 'bg-purple-600 text-white shadow-md shadow-purple-600/20'
                      : 'bg-slate-800/60 text-slate-400 hover:text-slate-200'
                  }`}
                >
                  Todos do Catálogo ({products.length})
                </button>
                <button
                  onClick={() => setStatusFilter('added')}
                  className={`px-3 py-1.5 rounded-lg text-[11px] font-black uppercase tracking-wider transition-all ${
                    statusFilter === 'added'
                      ? 'bg-purple-600 text-white shadow-md shadow-purple-600/20'
                      : 'bg-slate-800/60 text-slate-400 hover:text-slate-200'
                  }`}
                >
                  Já no Meu Estoque ({existingStock.length})
                </button>
              </div>

              <span className="text-[11px] text-slate-500 font-medium">
                Exibindo <strong className="text-slate-300">{filteredProducts.length}</strong> produtos
              </span>
            </div>
          </div>

          {/* Product List Content */}
          <div className="flex-1 overflow-y-auto p-4 sm:p-6 divide-y divide-slate-800/60">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-20 space-y-3">
                <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-purple-500"></div>
                <p className="text-xs text-slate-400 font-bold uppercase tracking-wider">Carregando catálogo...</p>
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="py-16 text-center space-y-3">
                <div className="w-14 h-14 rounded-2xl bg-slate-800/60 border border-slate-700/50 flex items-center justify-center mx-auto text-slate-600 text-2xl">
                  <i className="fas fa-box-open"></i>
                </div>
                <p className="text-sm font-bold text-slate-400">Nenhum produto encontrado com os filtros selecionados.</p>
                <p className="text-xs text-slate-600">Tente ajustar o termo de busca ou alternar a categoria e jogo.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredProducts.map((product) => {
                  const isInStock = existingStockMap.has(product.id);
                  const inStockData = existingStockMap.get(product.id);
                  const state = getFormState(product);
                  const msrp = product.msrp || product.mspr || 0;

                  return (
                    <div
                      key={product.id}
                      className={`p-3.5 sm:p-4 rounded-2xl border transition-all flex flex-col md:flex-row items-start md:items-center justify-between gap-4 ${
                        isInStock
                          ? 'bg-slate-900/40 border-slate-800/80 hover:border-slate-700'
                          : 'bg-slate-900/70 border-slate-800 hover:border-purple-500/30'
                      }`}
                    >
                      {/* Product Info */}
                      <div className="flex items-center gap-3.5 min-w-0 flex-1">
                        <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl bg-slate-950 border border-slate-800 shrink-0 overflow-hidden flex items-center justify-center p-1">
                          <img
                            src={product.image_url || 'https://images.unsplash.com/photo-1614850523296-d8c1af93d400?auto=format&fit=crop&q=80&w=200'}
                            alt={product.name}
                            className="w-full h-full object-contain"
                            loading="lazy"
                            onError={(e: any) => {
                              e.currentTarget.src = 'https://images.unsplash.com/photo-1614850523296-d8c1af93d400?auto=format&fit=crop&q=80&w=200';
                            }}
                          />
                        </div>

                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            {isProductPreSale(product) && (
                              <span className="text-[9px] font-black px-2 py-0.5 rounded-md bg-amber-500/15 text-amber-300 border border-amber-500/30 uppercase tracking-wider flex items-center gap-1 shadow-xs">
                                <i className="fas fa-clock text-[8px] text-amber-400"></i>
                                <span>Pré-Venda</span>
                              </span>
                            )}
                            {product.game_name && (
                              <span className="text-[9px] font-black px-2 py-0.5 rounded-md bg-purple-500/10 text-purple-400 border border-purple-500/20 uppercase tracking-wider">
                                {product.game_name}
                              </span>
                            )}
                            {product.product_type_name && (
                              <span className="text-[9px] font-black px-2 py-0.5 rounded-md bg-slate-800 text-slate-400 uppercase tracking-wider">
                                {product.product_type_name}
                              </span>
                            )}
                            {isInStock && (
                              <span className="text-[9px] font-bold px-2 py-0.5 rounded-md bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 flex items-center gap-1">
                                <i className="fas fa-check text-[8px]"></i>
                                No seu estoque ({inStockData?.quantity} un. • R$ {Number(inStockData?.price || 0).toFixed(2).replace('.', ',')})
                              </span>
                            )}
                          </div>

                          <h3 className="text-sm font-bold text-white truncate" title={product.beauty_name || product.name}>
                            {product.beauty_name || product.name}
                          </h3>

                          {product.release_date && isProductPreSale(product) && (
                            <p className="text-[10px] text-amber-400 font-bold flex items-center gap-1 mt-0.5">
                              <i className="fas fa-calendar-alt text-[8.5px]"></i>
                              <span>Lançamento: {new Date(product.release_date).toLocaleDateString('pt-BR')}</span>
                            </p>
                          )}

                          {msrp > 0 && (
                            <p className="text-[11px] text-slate-500 mt-0.5">
                              Preço sugerido (MSRP): <strong className="text-slate-400">R$ {Number(msrp).toFixed(2).replace('.', ',')}</strong>
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Inline Fast Add Form */}
                      <div className="flex items-center gap-3 w-full md:w-auto shrink-0 flex-wrap sm:flex-nowrap justify-end pt-2 md:pt-0 border-t md:border-t-0 border-slate-800/60">
                        {/* Quantity */}
                        <div className="space-y-1">
                          <label className="text-[9px] font-black uppercase tracking-wider text-slate-500 block">Qtd</label>
                          <div className="flex items-center bg-slate-950 border border-slate-800 rounded-xl overflow-hidden h-[40px] w-24">
                            <button
                              type="button"
                              onClick={() => handleFieldChange(product.id, 'quantity', Math.max(1, (Number(state.quantity) || 1) - 1))}
                              className="w-7 h-full text-slate-400 hover:text-white hover:bg-slate-800/60 transition-colors flex items-center justify-center text-xs"
                            >
                              -
                            </button>
                            <input
                              type="number"
                              min="1"
                              value={state.quantity}
                              onChange={(e) => handleFieldChange(product.id, 'quantity', Math.max(1, parseInt(e.target.value) || 1))}
                              className="w-10 bg-transparent text-center text-xs font-bold text-white focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            />
                            <button
                              type="button"
                              onClick={() => handleFieldChange(product.id, 'quantity', (Number(state.quantity) || 1) + 1)}
                              className="w-7 h-full text-slate-400 hover:text-white hover:bg-slate-800/60 transition-colors flex items-center justify-center text-xs"
                            >
                              +
                            </button>
                          </div>
                        </div>

                        {/* Price (R$) - Min 0.05 */}
                        <div className="space-y-1">
                          <div className="flex items-center justify-between">
                            <label className="text-[9px] font-black uppercase tracking-wider text-slate-500 block">Preço (R$)</label>
                            <span className="text-[8px] text-slate-500 ml-1">Mín. 0,05</span>
                          </div>
                          <div className={`flex items-center bg-slate-950 border rounded-xl px-2.5 h-[40px] w-28 transition-colors ${
                            state.price < 0.05 ? 'border-amber-500/50 bg-amber-950/20' : 'border-slate-800'
                          }`}>
                            <span className="text-slate-600 text-xs mr-1 font-bold">R$</span>
                            <input
                              type="number"
                              step="0.01"
                              min="0.05"
                              value={state.price}
                              onChange={(e) => handleFieldChange(product.id, 'price', parseFloat(e.target.value) || 0)}
                              className="w-full bg-transparent text-xs font-bold text-white focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            />
                          </div>
                        </div>

                        {/* Pre-sale Toggle */}
                        <div className="space-y-1">
                          <label className="text-[9px] font-black uppercase tracking-wider text-slate-500 block">Pré-venda</label>
                          <label className="flex items-center justify-center h-[40px] px-2.5 bg-slate-950 border border-slate-800 rounded-xl cursor-pointer hover:border-slate-700 transition-colors">
                            <input
                              type="checkbox"
                              checked={state.preSale}
                              onChange={(e) => handleFieldChange(product.id, 'preSale', e.target.checked)}
                              className="accent-purple-600 w-4 h-4 cursor-pointer"
                            />
                          </label>
                        </div>

                        {/* Action Button */}
                        <div className="space-y-1 pt-3 sm:pt-0">
                          <span className="text-[9px] font-black uppercase tracking-wider opacity-0 block">Ação</span>
                          <button
                            type="button"
                            disabled={state.saving}
                            onClick={() => handleQuickAdd(product)}
                            className={`h-[40px] px-4 rounded-xl text-xs font-black uppercase tracking-wider transition-all flex items-center justify-center gap-2 whitespace-nowrap active:scale-95 cursor-pointer ${
                              state.saved
                                ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-600/30'
                                : isInStock
                                  ? 'bg-slate-800 hover:bg-slate-700 text-white border border-slate-700 hover:border-purple-500'
                                  : 'bg-purple-600 hover:bg-purple-500 text-white shadow-lg shadow-purple-600/20 hover:shadow-purple-500/30'
                            }`}
                          >
                            {state.saving ? (
                              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                            ) : state.saved ? (
                              <>
                                <i className="fas fa-check"></i> Salvo!
                              </>
                            ) : isInStock ? (
                              <>
                                <i className="fas fa-arrows-rotate text-[10px]"></i> Atualizar
                              </>
                            ) : (
                              <>
                                <i className="fas fa-plus text-[10px]"></i> Adicionar
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-4 sm:p-5 border-t border-slate-800 bg-slate-900/95 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-2 text-xs text-slate-400">
              <i className="fas fa-info-circle text-purple-400"></i>
              <span>Você pode continuar adicionando produtos sem fechar esta janela.</span>
            </div>
            <button
              onClick={onClose}
              className="px-5 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-white text-xs font-black uppercase tracking-wider transition-colors cursor-pointer"
            >
              Concluir e Fechar
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};
